import { PlayerEvent } from 'bitmovin-player';
import { throttle } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import useAvod from '../../../hooks/useAvod';
import { playerTypes, NO_AD_CTA_AD_TYPES } from '../constants';
import { setYBAdPluginOptions } from '../../../helpers/youbora';
import playerService from '../../../helpers/bitmovinPlayer';
import { safePlayerPlay } from '../../../helpers/playerGuards';
import cdnHandler from '../../../helpers/CDNHandler';
import { avodMixpanelEvents } from '../../../helpers/avodHandler';
import { showUpgradedModal } from '../../../store/reducers/generalConfigurationSlice';
import { useConfigurationSections } from '../../../hooks/useConfiguration';
import useCustomSeekbar from '../utils/useCustomSeekbar';
import {
    getNoAdCtaAdBreakStartTime,
    hideNoAdCta,
    type NoAdCtaConfig,
    setNoAdCtaAdBreakStartTime,
    setupNoAdCtaFromConfig,
} from '../utils/AdsUiUtils';
import useResumeAdPlayback from './useResumeAdPlayback';
import { playerSSAIManager } from '../../../utils/SSAI';

export const useAds = (playerType, isTvod, VODAsset, bookmarkedVodTimeCode, uiManagerRef, setAutoUiVariantResolve) => {
    const { isAdsEnabled, updateAdsUI, onAdBreakEndUI, convertMsToTime } = useAvod(playerType, isTvod, VODAsset);
    const { config } = useConfigurationSections();
    const dispatch = useDispatch();
    const { setupCustomSeekbar } = useCustomSeekbar();
    const adsCount = useRef(0);
    // Screen 1: yellow mid-roll cue dots. Real break schedule from the manifest (onAdData).
    const adScheduleRef = useRef<Array<{ startPosition?: number; duration?: number }>>([]);
    const cueEnsureRef = useRef(false);

    /** Derives preroll|midroll|postroll from ad break position/startPosition. Used for No Ads CTA config lookup. */
    const getAdTypeFromBreak = (
        adBreak: {
            startPosition?: number;
            position?: string;
            adType?: string;
            ads?: Array<{ startPosition?: number }>;
        } | null
    ): string | undefined => {
        if (!adBreak) return undefined;
        const rawType = (adBreak as { position?: string }).position ?? adBreak.adType;
        if (rawType) {
            const n = String(rawType)
                .toLowerCase()
                .replace(/[-_\s]/g, '');
            if (n === 'preroll' || n === 'pre') return NO_AD_CTA_AD_TYPES.PREROLL;
            if (n === 'postroll' || n === 'post') return NO_AD_CTA_AD_TYPES.POSTROLL;
            if (n === 'midroll' || n === 'mid') return NO_AD_CTA_AD_TYPES.MIDROLL;
        }
        const startPos = adBreak.startPosition ?? adBreak.ads?.[0]?.startPosition ?? 0;
        const startSec = typeof startPos === 'number' ? startPos / 1000 : 0;
        if (startSec <= 0) return NO_AD_CTA_AD_TYPES.PREROLL;
        const duration = playerService.player?.getDuration();
        if (typeof duration === 'number' && duration > 0 && startSec >= duration - 1) {
            return NO_AD_CTA_AD_TYPES.POSTROLL;
        }
        return NO_AD_CTA_AD_TYPES.MIDROLL;
    };

    /** No Ads CTA: show/hide upgrade button per avodConfigs.noAdCta.byAdType[adType] */
    const setupNoAdCta = () => {
        const adsData = playerService.getAdsData();
        const adType = (adsData as { adType?: string })?.adType ?? getAdTypeFromBreak(adsData);
        setupNoAdCtaFromConfig(
            config as { avodConfigs?: { noAdCta?: NoAdCtaConfig } } | null,
            adType ?? NO_AD_CTA_AD_TYPES.PREROLL
        );
    };

    /**
     * Registers a `TimeChanged` listener that updates the custom playback time label
     * using content-only time (player time minus cumulative ad duration).
     *
     * Intended to run after ad breaks so the displayed current/total time excludes ads.
     */
    const updatePlaybackTimeExcludingAds = () => {
        const player = playerService.player;
        // TODO: should this be made more generic and not tied to ad break end? Where can it be moved?
        player?.on(PlayerEvent.TimeChanged, () => {
            const playerContainer = player?.getContainer();
            // TODO: `custom-playbacktime` selector is added in PlayerUiUtils. Maybe selector name should be moved to a
            // constant shared by both places to avoid duplication and potential mismatches?
            const customPlaybacktimeElement = playerContainer?.querySelector('.custom-playbacktime');
            const adsData = playerService.getAdsData();
            const totalAdsDuration = (adsData?.duration ?? 0) / 1000;

            if (!customPlaybacktimeElement) return;

            const totalTime = (player?.getDuration() ?? 0) - totalAdsDuration;
            const currentTime = (player?.getCurrentTime() ?? 0) - totalAdsDuration;

            customPlaybacktimeElement.textContent = `${convertMsToTime(
                currentTime * 1000,
                // Forcing hours display for current time if total duration is over 1 hour
                // `convertMsToTime` hides hours if duration provided as a first argument is under 1 hour which is the
                // case here since converting current time. But for movies which are longer than 1 hour we want to show
                // it as HH:mm:ss instead of mm:ss
                totalTime / 60 / 60 > 1
            )} / ${convertMsToTime(totalTime * 1000)}`;
        });
    };

    /**
     * Screen 1: draw small yellow cue dots on the content seekbar at each MID-ROLL break's real
     * position (from the manifest schedule via onAdData). The `custom-seekbar-ui` variant is plain
     * divs (not a real Bitmovin SeekBar), so `addTimelineMarker` can't populate it — we inject the
     * dot elements into whichever playback seekbar is currently visible. Pre-roll (t=0) and post-roll
     * (t~=duration) are skipped. Fully guarded so it can never affect ad playback.
     */
    const placeAdCueDots = (): boolean => {
        try {
            const player = playerService.player;
            const duration = player?.getDuration?.();
            const container = player?.getContainer?.();
            if (!player || !container || !duration || !Number.isFinite(duration) || duration <= 0) return false;
            // The custom seekbar (and the manifest break positions) are CONTENT time, so place dots as
            // a fraction of content duration (= stitched getDuration minus total ad duration), not of
            // getDuration — otherwise every dot sits slightly left, growing toward the end.
            const totalAdSec = adScheduleRef.current.reduce((s, b) => s + (b.duration ?? 0) / 1000, 0);
            const contentDuration = Math.max(1, duration - totalAdSec);
            const times = Array.from(
                new Set(
                    adScheduleRef.current
                        .map((brk) => Math.floor((brk.startPosition ?? 0) / 1000))
                        .filter((t) => t > 0 && t < contentDuration - 1)
                )
            );
            if (!times.length) return false;

            // Self-heal: the player swaps seekbar variants on every ad break, so re-check on each
            // (throttled) TimeChanged and re-inject the dots into the new visible seekbar if missing.
            if (!cueEnsureRef.current) {
                cueEnsureRef.current = true;
                player.on(
                    PlayerEvent.TimeChanged,
                    throttle(() => placeAdCueDots(), 1000)
                );
            }

            // The visible playback seekbar's markers layer (exclude the volume slider's seekbar).
            const markersLayer = Array.from(container.querySelectorAll('[class*="seekbar-markers"]'))
                .filter((c) => !(c as HTMLElement).closest('[class*="volumeslider"]'))
                .find((c) => (c as HTMLElement).offsetParent !== null) as HTMLElement | undefined;
            if (!markersLayer) return false; // no visible seekbar yet → let the caller retry

            // already injected into this seekbar? skip (prevents the per-second churn)
            if (markersLayer.querySelectorAll('.bmpui-ad-cue-marker').length === times.length) return true;

            markersLayer.querySelectorAll('.bmpui-ad-cue-marker').forEach((el) => el.remove());
            times.forEach((t) => {
                const pct = Math.min(100, Math.max(0, (t / contentDuration) * 100));
                const dot = document.createElement('div');
                dot.className = 'bmpui-ad-cue-marker';
                // layout is positional (left = % of seekbar); appearance lives in _ads.sass
                dot.style.position = 'absolute';
                dot.style.left = `${pct}%`;
                dot.style.top = '50%';
                dot.style.transform = 'translate(-50%, -50%)';
                markersLayer.appendChild(dot);
            });
            return true;
        } catch {
            return true;
        }
    };

    /** Place the cue dots, retrying until the player/seekbar is ready (onAdData fires early). */
    const seedAdCueDots = () => {
        let attempts = 0;
        const tick = () => {
            if (playerSSAIManager.hasActiveAds || placeAdCueDots() || ++attempts > 40) return;
            setTimeout(tick, 300);
        };
        tick();
    };

    /** Receives the ad-break schedule (from onAdData) and (re)seeds the cue dots. */
    const onCueDotsSchedule = (adList: Array<{ startPosition?: number; duration?: number }>) => {
        adScheduleRef.current = Array.isArray(adList) ? adList : [];
        seedAdCueDots();
    };

    /**
     * `player` must be the freshly created instance: `playerService.player` is only set once the
     * player fires `loadeddata`, so it is still null (or a destroyed instance) when this runs.
     */
    const handleAVODEvents = (player) => {
        if (
            !player ||
            playerType !== playerTypes.Vod ||
            !isAdsEnabled ||
            bookmarkedVodTimeCode() > 0 ||
            playerSSAIManager.hasActiveAds
        ) {
            return;
        }

        const togglePlaybackOnVisibilityChange = function () {
            const player = playerService.player;
            if (!player) return;

            if (document.hidden) {
                // Tab is inactive - pause the player (content or ad)
                if (player.isPlaying()) {
                    player.pause();
                }
            } else {
                // Tab is active again - resume the player
                // Optionally check if it was playing before to avoid auto-playing
                // a user-paused video.
                safePlayerPlay(player);
            }
        };

        setYBAdPluginOptions(player);
        cdnHandler.setupAdvertisingEvents({
            onAdBreakBegin: (adEvent) => {
                if (playerSSAIManager.hasActiveAds) return;
                const t = playerService.player?.getCurrentTime() ?? 0;
                setNoAdCtaAdBreakStartTime(typeof t === 'number' ? t : 0);
                const adUiVariant = uiManagerRef.current.getUiVariants().find((v) => v.ui.getConfig().id === 'ads-ui');
                if (adUiVariant) uiManagerRef.current.switchToUiVariant(adUiVariant);
                const likeDislikeBtns: HTMLDivElement | null = document.querySelector('*[class*="like_likes"');
                if (likeDislikeBtns) likeDislikeBtns.style.display = 'none';
                adsCount.current = adEvent.ads.length;
                updateAdsUI(null, adsCount.current, adEvent.duration);
                hideNoAdCta();
                const adType = getAdTypeFromBreak(adEvent) ?? NO_AD_CTA_AD_TYPES.PREROLL;
                playerService.setAdsData({ ...adEvent, adType });
                setupNoAdCta();
                avodMixpanelEvents.onAdStart();
                adEvent.ads.forEach((ad, index) => {
                    if (typeof ad.startPosition === 'number' && typeof ad.duration === 'number') {
                        uiManagerRef.current?.addTimelineMarker({
                            time: Math.floor(ad.startPosition / 1000),
                            duration: Math.floor(ad.duration / 1000),
                            title: ad?.title || `Marker #${index + 1}`,
                        });
                    }
                });

                // TODO: move to BaseBitmovinPlayer
                document.addEventListener('visibilitychange', togglePlaybackOnVisibilityChange);
            },
            onAdBegin: (adEvent) => {
                if (playerSSAIManager.hasActiveAds) return;
                const likeDislikeBtns: HTMLDivElement | null = document.querySelector('*[class*="like_likes"');
                if (likeDislikeBtns) likeDislikeBtns.style.display = 'none';
                updateAdsUI(adEvent.adNumber || adEvent.index + 1, adsCount.current);
            },
            onAdBreakEnd: (adEvent) => {
                if (playerSSAIManager.hasActiveAds) return;
                const customSeekbarUI = uiManagerRef.current
                    .getUiVariants()
                    .find((v) => v.ui.getConfig().id === 'custom-seekbar-ui');
                if (customSeekbarUI) {
                    uiManagerRef.current.switchToUiVariant(customSeekbarUI, () =>
                        setupCustomSeekbar(playerService.player)
                    );
                }
                setAutoUiVariantResolve(true);
                onAdBreakEndUI(playerService.player);
                playerService.setIsAdPlayed(true);
                avodMixpanelEvents.onAdCompleted();
                const currentMarkers = uiManagerRef.current.getTimelineMarkers();
                adEvent.ads.forEach((ad) => {
                    const marker = currentMarkers.find((m) => m.time === Math.floor(ad.startPosition / 1000));
                    if (marker) uiManagerRef.current.removeTimelineMarker(marker);
                });
                avodMixpanelEvents.onVideoStarted();
                const likeDislikeBtns: HTMLDivElement | null = document.querySelector('*[class*="like_likes"');
                if (likeDislikeBtns) likeDislikeBtns.style.display = 'flex';
                hideNoAdCta();

                updatePlaybackTimeExcludingAds();

                document.removeEventListener('visibilitychange', togglePlaybackOnVisibilityChange);
            },
            onAdData: (adList: any) => {
                if (playerSSAIManager.hasActiveAds) return;
                onCueDotsSchedule(adList);
            },
        });
    };

    useResumeAdPlayback();

    // When user proceeds to upgrade (redirects) then returns without completing, restart ad from beginning
    useEffect(() => {
        const handlePageShow = (event: PageTransitionEvent) => {
            if (sessionStorage.getItem('noAdCta_proceededToUpgrade') !== '1') return;

            sessionStorage.removeItem('noAdCta_proceededToUpgrade');
            dispatch(showUpgradedModal(false));

            const player = playerService.player;
            if (!player) return;

            if (event.persisted) {
                // Page restored from bfcache - player still exists, restart ad from beginning
                const restartTime = getNoAdCtaAdBreakStartTime() ?? 0;
                try {
                    player.seek(restartTime);
                    safePlayerPlay(player);
                } catch {
                    // ignore
                }
            }
            // If not persisted (full reload), stream restarts naturally with fresh ad break
        };

        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, [dispatch]);

    const handlePlayerOnAdSeeked = useCallback(
        (data) => {
            if (!playerSSAIManager.hasActiveAds && isAdsEnabled) {
                const adsTotalDuration = playerService.getAdsData()?.duration / 1000;
                const isAdPlayed = playerService.getIsAdPlayed();

                if (adsTotalDuration != null && data.seekTarget < adsTotalDuration && isAdPlayed) {
                    // After ad has finished playing, when user seeks back into ad break, player should seek to the
                    // beginning of the main content (instead of seeking within the ad break which is already played)
                    // For some reason, without timeout seek doesn't work as expected and player returns back to ad break.
                    // TODO: investigate if this is a player issue and if there's a better way to handle it than using
                    // setTimeout
                    setTimeout(() => {
                        playerService.player.seek(adsTotalDuration + 1);
                    }, 50);
                }
            }
        },
        [isAdsEnabled]
    );

    return {
        handleAVODEvents,
        handlePlayerOnAdSeeked,
    };
};
